import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  DotsSixVertical,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_TILES, TILE_ICONS, TILE_KINDS } from "@/lib/contactTiles";

const selectCls =
  "h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-400";

const blank = () => ({ kind: "custom", icon: "chat", label: "", value: "", href: "", show: true });

export const ContactTilesEditor = ({ tiles, onChange, testId = "site-contact-tiles" }) => {
  const rows = Array.isArray(tiles) ? tiles : DEFAULT_TILES;
  const [dragIdx, setDragIdx] = useState(null);
  const patch = (i, key, value) =>
    onChange(rows.map((t, n) => (n === i ? { ...t, [key]: value } : t)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const drop = (i) => {
    if (dragIdx === null || dragIdx === i) {
      setDragIdx(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onChange(next);
    setDragIdx(null);
  };

  const visible = rows.filter((t) => t.show !== false);

  return (
    <div className="space-y-3" data-testid={testId}>
      {rows.map((t, i) => {
        const kind = TILE_KINDS.find(([k]) => k === t.kind) || TILE_KINDS[4];
        const custom = kind[0] === "custom";
        const Icon = TILE_ICONS[t.icon] || TILE_ICONS[kind[2]];
        return (
          <div
            key={`${testId}-${i}`}
            data-testid={`${testId}-row-${i}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(i)}
            className={`rounded-md border bg-zinc-950 p-4 transition-colors ${
              dragIdx === i ? "border-indigo-500/60" : "border-zinc-800"
            } ${t.show === false ? "opacity-60" : ""}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnd={() => setDragIdx(null)}
                data-testid={`${testId}-drag-${i}`}
                title="Drag to reorder"
                className="flex h-10 w-6 shrink-0 cursor-grab items-center justify-center text-zinc-600 hover:text-zinc-300 active:cursor-grabbing"
              >
                <DotsSixVertical size={16} weight="bold" />
              </span>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-zinc-800 text-zinc-200">
                <Icon size={18} />
              </span>
              <select
                data-testid={`${testId}-kind-${i}`}
                value={kind[0]}
                onChange={(e) => patch(i, "kind", e.target.value)}
                className={`${selectCls} min-w-[200px] flex-1`}
              >
                {TILE_KINDS.map(([k, name]) => (
                  <option key={k} value={k}>{name}</option>
                ))}
              </select>
              <select
                data-testid={`${testId}-icon-${i}`}
                value={t.icon || ""}
                onChange={(e) => patch(i, "icon", e.target.value)}
                className={`${selectCls} w-36`}
                title="Icon"
              >
                <option value="">Default icon</option>
                {Object.keys(TILE_ICONS).map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <span className="ml-auto flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Show
                <Switch
                  data-testid={`${testId}-show-${i}`}
                  checked={t.show !== false}
                  onCheckedChange={(v) => patch(i, "show", v)}
                />
              </span>
              <button type="button" data-testid={`${testId}-up-${i}`} onClick={() => move(i, -1)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100">
                <ArrowUp size={13} weight="bold" />
              </button>
              <button type="button" data-testid={`${testId}-down-${i}`} onClick={() => move(i, 1)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100">
                <ArrowDown size={13} weight="bold" />
              </button>
              <button type="button" data-testid={`${testId}-remove-${i}`}
                onClick={() => onChange(rows.filter((_, n) => n !== i))}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300">
                <Trash size={13} weight="bold" />
              </button>
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_2fr]">
              <Input
                data-testid={`${testId}-label-${i}`}
                value={t.label || ""}
                onChange={(e) => patch(i, "label", e.target.value)}
                placeholder={custom ? "Small label, e.g. Text us" : `Label (default: ${kind[3]})`}
                className="h-10 border-zinc-800 bg-zinc-900 text-sm"
              />
              <Textarea
                data-testid={`${testId}-value-${i}`}
                rows={custom ? 2 : 1}
                value={t.value || ""}
                onChange={(e) => patch(i, "value", e.target.value)}
                placeholder={custom ? "What the tile says (new lines allowed)" : "Leave blank to use My Company details"}
                className="min-h-[40px] border-zinc-800 bg-zinc-900 text-sm"
              />
            </div>
            <Input
              data-testid={`${testId}-href-${i}`}
              value={t.href || ""}
              onChange={(e) => patch(i, "href", e.target.value)}
              placeholder={custom ? "Link (optional) — https://…, tel:…, sms:…, mailto:…" : "Link override (optional) — auto maps / tel / mailto when blank"}
              className="mt-2 h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
          </div>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <Button type="button" data-testid={`${testId}-add`} onClick={() => onChange([...rows, blank()])}
          className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
          <Plus size={13} weight="bold" className="mr-2" /> Add tile
        </Button>
        <Button type="button" data-testid={`${testId}-reset`} onClick={() => onChange(DEFAULT_TILES.map((t) => ({ ...t })))}
          className="h-10 border border-zinc-800 bg-transparent text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
          Reset to defaults
        </Button>
      </div>

      <div
        data-testid={`${testId}-preview`}
        className="rounded-md border border-zinc-800 bg-zinc-950 p-4"
      >
        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          Live preview — how the tiles appear on the contact page
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {visible.length === 0 && (
            <p className="text-xs text-zinc-600">No tiles are shown right now.</p>
          )}
          {visible.map((t, i) => {
            const kind = TILE_KINDS.find(([k]) => k === t.kind) || TILE_KINDS[4];
            const Icon = TILE_ICONS[t.icon] || TILE_ICONS[kind[2]];
            const label = (t.label || "").trim() || kind[3] || "Untitled";
            const value = (t.value || "").trim();
            const isAuto = kind[0] !== "custom";
            return (
              <div
                key={i}
                data-testid={`${testId}-preview-tile-${i}`}
                className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-300">
                  <Icon size={16} weight="bold" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    {label}
                  </p>
                  <p className="mt-0.5 whitespace-pre-line break-words text-sm text-zinc-200">
                    {value ? (
                      value
                    ) : isAuto ? (
                      <span className="italic text-zinc-500">From My Company</span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
